import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { f2 } from '../copyFlow2'
import { useAside, type BrowseRes, type Card } from '../lib/browse'
import { filterSearch, homeArea, useBrowse, useFilters, useKeepScroll, useScrolledPast, useScrollToResults } from '../lib/useBrowse'
import { areaLabel, FilterBar, FilterSummary, hasArea, resolvedPlace } from '../components/browse/FilterBar'
import { CardSkeleton, ListingCard } from '../components/browse/ListingCard'
import { photoRow } from '../components/browse/Photo'
import { Grid, Moods, resultsLabel, Row } from '../components/browse/Rails'
import { FoundDoor, EmptyActions } from '../components/browse/V2Bits'
import { Link } from 'react-router-dom'
import { dateRange, NO_FILTERS } from '../lib/browse'
import { placeInRange } from '../lib/fit'
import { cachedProfile, firstRunDoneHere, localSetup, setupPlan } from '../lib/v2'
import { canonLabelKey, reasonKey } from '../lib/categories'
import { useLocation, useNavigate } from 'react-router-dom'
import { BrowseShell } from '../components/browse/Shell'
import { CitySetupState } from '../components/browse/CityState'
import { ChangeCity, CitySwitcher } from '../components/CitySwitcher'
import { useCitySwitcher } from '../lib/cities'
import { TraySpacer } from '../components/browse/BrowseTray'
import { v3 } from '../copyV3'
import { groupRepeats } from '../lib/repeats'
import { placeName } from '../lib/saved'
import { buttonClass } from '../ui/styles'
import { Spinner } from '../ui/Spinner'
import { FADE } from '../ui/motion'
import { SendFeedback } from '../components/SendFeedback'
import { CheckInCard } from '../components/CheckIn'
import { WeekendEntry } from '../components/WeekendEntry'
import { TasteEntry } from '../components/TasteEntry'
// features 10/16 (Browse lane): map view (R-06), ask in your own words (R-05), Go with others (R-30), behind lib/flags.ts
import { useSearchParams } from 'react-router-dom'
import { flag, useFlag } from '../lib/flags'
import { fb } from '../copyFeatBrowse'
import { AskBar } from '../components/AskBar'
import { AskResults } from '../components/browse/AskResults'
import { BrowseMap } from '../components/browse/BrowseMap'
import { ViewToggle, type View } from '../components/PhoneMap'
import { askBrowse, clearAsk, useAskBrowse } from '../lib/askBrowse'
import { keepsWords } from '../lib/words'
import { isUnderTheRadar } from '../lib/format'
import { useTaste } from '../lib/tasteFeedback'

/** Server row keys -> approved titles (flow3 §1); `outdoors` reads as its category name. */
export const ROW_TITLE: Record<string, string> = {
  for_plan: 'browse.picked',
  for_you: 'browse.for_you',
  weekend: 'browse.weekend',
  near: 'browse.near',
  free: 'browse.free',
  hidden: 'browse.radar',
  classes: 'browse.classes',
  with_people: 'browse.people',
  outdoors: 'category.outdoors',
}
export const rowTitle = (key: string) => (key === 'with_people' && flag('go_with_others') ? fb('fb.people.title') : ROW_TITLE[key] ? f2(ROW_TITLE[key]) : key)
/** R-30: the line under a row's title (Go with others says you would be doing these alongside other people). */
export const rowNote = (key: string) => (key === 'with_people' && flag('go_with_others') ? fb('fb.people.note') : null)

/** Up to six previews a row; a card already previewed in an earlier row (or set aside) is not previewed again.
 *  `forYou`: someone with a profile always gets a For you row first (the ranked list's top 8 when the server sent none). */
function previews(res: BrowseRes, asideIds: Set<string>, forYou = false, keep: (c: Card) => boolean = () => true): { key: string; cards: Card[] }[] {
  const used = new Set<string>()
  const out: { key: string; cards: Card[] }[] = []
  const rows = forYou && !res.rows.some((r) => r.key === 'for_you') && res.items.length ? [{ key: 'for_you', items: res.items.slice(0, 8) }, ...res.rows] : res.rows
  for (const row of rows) {
    const cards = row.items.filter((c) => keep(c) && !used.has(c.item.id) && !asideIds.has(c.item.id)).slice(0, 6)
    if (!cards.length) continue
    cards.forEach((c) => used.add(c.item.id))
    out.push({ key: row.key, cards })
  }
  return out
}

/** Browse `/` (flow3 §1): everything shows by default; where, distance, when and filters only narrow it. */
export default function Browse() {
  const [f, setF] = useFilters()
  const navigate = useNavigate()
  const { res, stale, placeFresh, error, errorStatus, loading, slow, more, loadMore, retry, refresh } = useBrowse(f)
  // R-06: List / Map, kept in the URL (view=map) so closing a detail opened from a pin comes back to the map
  const [sp] = useSearchParams()
  const mapOn = useFlag('map_view')
  const view_: View = mapOn && sp.get('view') === 'map' ? 'map' : 'list'
  const setView = (v: View) => {
    const next = new URLSearchParams(sp)
    if (v === 'map') next.set('view', 'map'); else next.delete('view')
    next.delete('item')
    navigate({ search: next.toString() }, { replace: true, preventScrollReset: true })
  }
  const toggle = mapOn ? <ViewToggle view={view_} onView={setView} className="w-[164px] shrink-0" /> : null
  // B-069: a list for the previous filters never shows under the new ones; the loading row stands in until it lands
  const view = stale ? null : res
  // review M5: Picked for this plan with nothing that close: the same plan answers again with no distance
  const widenPlan = async () => {
    if (!f.plan) return
    const kept = localSetup(f.plan)
    if (!kept) { navigate('/plan/new'); return }
    try {
      await setupPlan({ ...kept, radius_mi: null })
      retry()
    } catch { navigate('/plan/new') }
  }
  // review H1: a plan that is gone or not theirs (404 / 401) drops the plan from the view and says so
  const planLost = Boolean((useLocation().state as { planLost?: boolean } | null)?.planLost)
  useEffect(() => {
    if (f.plan && (errorStatus === 404 || errorStatus === 401)) {
      navigate({ pathname: '/', search: filterSearch({ ...f, plan: null }) }, { replace: true, state: { planLost: true } })
    }
  }, [f.plan, errorStatus]) // eslint-disable-line react-hooks/exhaustive-deps
  // fr3 (Alvin, 10/08: "I spent the energy to go through onboarding... It should give me something in return"): with a
  // finished first run, Browse is a For you feed: a home chip instead of the Where field, For you first with a reason on
  // each card, the time rows, then the category tiles lower down. Visitors with no profile keep the plain Browse.
  const prof = cachedProfile()
  const mine = !f.plan && firstRunDoneHere() && Boolean(prof)
  const home = mine ? prof?.home?.label ?? null : null
  // QA prod r1 (B-067): with no place typed, the saved home IS the area: distance shown on cards, slider live
  const atHome = mine && homeArea(f) !== null
  const aside = useAside()
  // R-05: ask in your own words (signed in with a profile: the ask function ranks by their taste)
  const askOn = useFlag('ask_browse') && firstRunDoneHere() && Boolean(prof)
  const askState = useAskBrowse()
  const asking = askOn && askState.status !== 'idle'
  const [askRound, setAskRound] = useState(0)
  const { hidden: nfm } = useTaste()
  const wordsOn = useFlag('words_filter')
  // the ask answers in words; the filters that can be read off a card still hold (free, category, under the radar)
  const askCards = asking ? askState.cards.filter((c) => (!wordsOn || keepsWords(c.item, f)) && !nfm.has(c.item.id)
    && (!f.free || c.item.is_free) && (!f.cats.length || f.cats.includes(c.item.category)) && (!f.hidden || isUnderTheRadar(c.item))) : []
  const endAsk = () => { clearAsk(); setAskRound((r) => r + 1) }
  const askBar = (className: string) => (
    <AskBar key={askRound} className={className} initial={askState.q} busy={askState.status === 'asking'}
      placeholder={fb('fb.ask.placeholder')} onAsk={(q) => void askBrowse(q, f.plan)} />
  )
  const asideIds = useMemo(() => new Set(aside.map((a) => a.id)), [aside])
  const [barRef, past] = useScrolledPast<HTMLDivElement>()
  useScrollToResults(stale, barRef)
  useKeepScroll(!!res)
  const city = res?.city?.label ?? 'Los Angeles'
  const citiesOn = useCitySwitcher() // Round 3 Lane 6
  const [cityOpen, setCityOpen] = useState(false)
  // R10: the field, the summary and the slider's helper read the place the server resolved ("Playa Vista 90094")
  // Alvin 10/09: a place in another city reads as that city ("Fort Lee", not the geocoder's "Whiteman Plaza 07024")
  const homeSlug = prof?.home?.city ?? 'los-angeles'
  const otherCity = placeFresh && hasArea(f) && res?.city && res.city.slug !== homeSlug ? res.city.label : null
  const centerLabel = placeFresh ? otherCity ?? res?.center?.label ?? null : null
  const area = home && !hasArea(f) ? home : areaLabel(f, centerLabel, city)
  const resolved = resolvedPlace(f, centerLabel)
  // Alvin 10/09: the hero names "the town that you're actively searching in or planning in": the plan's place, the place
  // typed or filtered to, else their home; the pool's city ("Los Angeles" = the whole metro) only when nothing more
  // specific is known
  const heroPlace = placeName(
    (f.plan ? res?.plan?.label : null)
    || otherCity
    || resolved
    || f.where.trim()
    || (f.lat !== null && f.lng !== null && placeFresh ? res?.center?.label : null)
    || (home && !hasArea(f) ? home : null)
    || (!f.plan && !hasArea(f) ? prof?.home?.label : null)
    || city,
  )
  // a typed place we could not match (or a country): back to their home view, with a line saying so (Alvin 10/09)
  const [missed, setMissed] = useState<{ text: string; kind: 'unknown' | 'need_city' } | null>(null)
  const rs = placeFresh && f.where.trim() ? res?.resolve?.status : undefined
  useEffect(() => {
    if (rs !== 'unknown' && rs !== 'need_city') return
    setMissed({ text: f.where.trim(), kind: rs })
    setF({ where: '', lat: null, lng: null, miles: null })
  }, [rs]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (f.where.trim() || f.lat !== null) setMissed(null) }, [f.where, f.lat])
  const showDistance = hasArea(f) || atHome
  const interests = prof?.interests ?? []
  const reason = (c: Card) => {
    const k = reasonKey(c.item, interests)
    if (!k) return null
    const name = f2(canonLabelKey(k))
    return f2('home.because', { interest: name.charAt(0).toLowerCase() + name.slice(1) })
  }
  // QA r2: a set-dates place (First Thursday ArtWalk, monthly bike ride) only shows for dates when one of its dates is
  // inside them (shared rule: supabase/functions/_shared/schedule.ts; the server applies it too once browse is deployed)
  const range = dateRange(f)
  const inRange = (c: Card) => !range || placeInRange(c.item, range.from, range.to)
  const rows = view ? previews(view, asideIds, mine, inRange) : []
  // fr4: while their home area is still building, ask again every 20 s for up to 3 minutes (V2-API-CHANGES §4); new cards slide in
  const areaBuilding = mine && Boolean(res?.area_building || res?.area?.state === 'building')
  const since = useRef(Date.now())
  useEffect(() => {
    if (!areaBuilding || Date.now() - since.current > 180_000) return
    const t = window.setTimeout(refresh, 20_000)
    return () => window.clearTimeout(t)
  }, [areaBuilding, res]) // eslint-disable-line react-hooks/exhaustive-deps
  const seenIds = useRef<Set<string> | null>(null)
  const shown = rows.flatMap((r) => r.cards.map((c) => c.item.id))
  const fresh = seenIds.current ? new Set(shown.filter((id) => !seenIds.current!.has(id))) : new Set<string>()
  useEffect(() => { if (res) seenIds.current = new Set([...(seenIds.current ?? []), ...shown]) }) // eslint-disable-line react-hooks/exhaustive-deps
  const all = (view?.items ?? []).filter(inRange)
  const cards = useMemo(() => [...all, ...(res?.rows ?? []).flatMap((r) => r.items), ...askState.cards], [res, askState]) // eslint-disable-line react-hooks/exhaustive-deps
  const search = filterSearch(f)
  const building = !stale && res?.resolve && ['new', 'building', 'failed'].includes(res.resolve.status) ? res.resolve : null
  const askInRow = !asking && rows.length > 0 && !(building && all.length === 0) // phone: the ask bar under the first row
  // a place being built: ask again quietly every 10 s for up to 3 minutes, so activities show as they arrive (Alvin 10/09)
  const buildSince = useRef(0)
  useEffect(() => {
    if (!building) { buildSince.current = 0; return }
    buildSince.current ||= Date.now()
    if (Date.now() - buildSince.current > 180_000) return
    const t = window.setTimeout(refresh, 10_000)
    return () => window.clearTimeout(t)
  }, [building?.slug, res]) // eslint-disable-line react-hooks/exhaustive-deps
  // the place they just typed, while its answer is still on the way (a new place can take 15 s to look up)
  const looking = loading && f.where.trim() && !placeFresh ? f.where.trim() : null
  // a typed place the server could not use keeps Los Angeles listings (resolve unknown / need_city / limit): say so
  // one hello right after See my activities. Blind r4 (kim, priya): it used to vanish on the first scroll or Save, which
  // pulled every card under it up 56-80 px, so the next tap saved the wrong card. It now stays put for this visit and is
  // dropped from the history entry at once, so a reload or Back to Browse does not say hello again.
  const loc = useLocation()
  const [welcome] = useState(() => mine && Boolean((loc.state as { welcome?: boolean } | null)?.welcome))
  useEffect(() => {
    if (!welcome) return
    try {
      const h = window.history.state as { usr?: { welcome?: boolean } } | null
      if (h?.usr?.welcome) window.history.replaceState({ ...h, usr: { ...h.usr, welcome: undefined } }, '')
    } catch { /* the line just shows again on reload */ }
  }, [welcome])
  // B-116 (desktop, slow This weekend): while the answer for new filters is on the way the list area keeps the height it
  // had, so the page does not shrink and grow back and nothing moves under the cursor; the loading row stands in at the top
  const listRef = useRef<HTMLDivElement>(null)
  const lastH = useRef(0)
  useLayoutEffect(() => { if (view && listRef.current) lastH.current = listRef.current.offsetHeight })
  const holdH = !view && loading && lastH.current ? lastH.current : undefined
  // the loading row stands where the first row was and as tall as it, so the mood rail under it keeps its place
  const firstRowRef = useRef<HTMLDivElement>(null)
  const firstH = useRef(0)
  useLayoutEffect(() => { if (view && firstRowRef.current) firstH.current = firstRowRef.current.offsetHeight })
  const whereNote = missed
    ? missed.kind === 'need_city' ? v3('v3.search_need_city', { place: missed.text }) : v3('v3.search_not_found', { text: missed.text, home: placeName(home ?? city) })
    : placeFresh && res?.resolve?.status === 'limit' ? f2('filter.location_error') : null

  return (
    <BrowseShell cards={cards} showDistance={showDistance}>
      <FilterSummary f={f} area={area} onChange={setF} visible={past} resolved={resolved} home={home} />
      <main className="mx-auto w-full max-w-md px-5 pb-8 md:max-w-[704px] md:px-8 lg:max-w-[1280px] lg:px-8">
        <section data-review-id="browse-header" className="pt-4 lg:pt-12">
          <h1 className="t-title text-ink">{f2('browse.headline')}</h1>
          {/* blind test r2: one noun (itinerary, Astra r4); the sub line hides on a phone for someone with a feed so the
              first picks show above the fold; the consent line moved to the first Save tap (ListingActions) */}
          <p data-review-id="browse-sub" className={`t-body mt-2 text-ink-2 ${mine ? 'hidden md:block' : ''}`}>{v3('v3.browse_sub_city', { city: heroPlace })}</p>
          {/* Round 3 Lane 6 (R-41): pick another city; it goes through the Where search (a city we have shows at once) */}
          {/* gate 10/16 (J1 fold): on a phone it moves under the first row, like WeekendEntry; it stays here when there are no
              rows (a city getting ready, nothing found), so the way back is never lost */}
          {citiesOn && !f.plan && <ChangeCity className={rows.length > 0 ? 'hidden lg:inline-flex' : 'inline-flex'} onClick={() => setCityOpen(true)} />}
          {citiesOn && <CitySwitcher open={cityOpen} onClose={() => setCityOpen(false)} current={placeFresh || !f.where ? res?.city?.slug ?? null : null} home={home} homeSlug={homeSlug}
            onPick={(w) => setF({ where: w ?? '', lat: null, lng: null, miles: null, anyDist: false })} />}
          {f.plan && (
            <p data-review-id="picked-banner" className="t-meta mt-4 flex flex-wrap items-center gap-x-3 border-l-[3px] border-accent pl-3 text-ink">
              <span className="font-semibold">{f2('browse.picked')}{res?.plan?.label ? ` · ${res.plan.label}` : ''}</span>
              <Link to={{ pathname: '/', search: filterSearch({ ...f, plan: null }) }} className="press inline-flex min-h-11 items-center text-accent underline underline-offset-4">{f2('browse.picked_clear')}</Link>
            </p>
          )}
        </section>

        {/* R-14: "Did you go?" after outings whose time has passed, once per app open (lib/checkin.ts) */}
        <CheckInCard />

        <div ref={barRef} className="mt-5 lg:mt-12">
          <FilterBar f={f} onChange={setF} area={area} resolved={resolved} home={home} />
          {whereNote && <p role="status" data-review-id="where-note" className={missed ? 't-body mt-3 border-l-[3px] border-accent pl-3 text-ink' : 't-meta mt-2 text-ink-2'}>{whereNote}</p>}
          {planLost && !f.plan && <p role="alert" data-review-id="plan-lost" className="t-meta mt-2 text-error">{f2('browse.error')}</p>}
          {/* gate 10/16 (journey J1): on a phone the ask bar sits under the first row and its mood tiles, so an activity
              stays above the fold; it comes back up here while an answer is showing (the rows are gone then) */}
          {askOn && askBar(askInRow ? 'mt-4 hidden lg:block' : 'mt-4')}
        </div>

        {asking && <AskResults ask={askState} cards={askCards} onClear={endAsk} showDistance={showDistance} map={view_ === 'map'} toggle={toggle} />}

        {!asking && building && (
          <CitySetupState slug={building.slug ?? ''} label={placeName(res?.city?.label ?? building.label ?? f.where)} status={building.status} onReady={retry} found={view?.total ?? 0} />
        )}
        {asking || (building && all.length === 0) ? null : (
          <motion.div ref={listRef} style={{ minHeight: holdH }} animate={{ opacity: loading && res ? 0.55 : 1 }} transition={loading ? FADE.exit : FADE.enter} aria-busy={loading || undefined}
            className="mt-6 space-y-8 lg:mt-12 lg:space-y-12">
            {welcome && <p role="status" data-review-id="for-you-welcome" className="t-body border-l-[3px] border-accent pl-3 text-ink">{f2('home.welcome')}</p>}

            {!view && loading && <LoadingRow slow={slow} text={looking ? v3('v3.search_looking', { place: looking }) : undefined} minHeight={firstH.current || undefined} />}
            {/* R-60: seven mood collections in place of the category tiles (categories stay in Filters and /category/:slug).
                Blind round 1 (B-108): it used to sit under EVERY row for a first-run person, so it dropped far out of view once
                picks landed. Now one compact rail right after the first row (after the loading row while loading, which is about
                a row tall), the same spot both ways and within one scroll; not above the rows, so an activity name stays above
                the fold at 375 x 812 (J1, B-082). */}
            {!building && !(rows.length > 0) && !error && <Moods counts={view?.collections ?? null} search={search} loading={loading} />}
            {error && !loading && (
              <div role="alert" className="border border-line bg-surface p-5">
                <p className="t-body text-ink">{f2('browse.error')}</p>
                <button type="button" onClick={retry} className={buttonClass('secondary', 'md', 'mt-3')}>{f2('browse.retry')}</button>
              </div>
            )}

            {rows.map((row, i) => (
              <div key={row.key} className="space-y-8 lg:space-y-12">
                <div ref={i === 0 ? firstRowRef : undefined}><Row id={row.key} title={mine && row.key === 'for_you' ? f2('home.for_you_title') : rowTitle(row.key)} cards={row.cards} showDistance={showDistance}
                  note={rowNote(row.key)} seeAll={{ pathname: `/row/${row.key}`, search }} reason={mine && row.key === 'for_you' ? reason : undefined} isNew={(c) => fresh.has(c.item.id)} /></div>
                {i === 0 && !building && <Moods counts={view?.collections ?? null} search={search} loading={loading} />}
                {i === 0 && askOn && askInRow && askBar('lg:hidden')}
                {/* growth/taste doors sit after the first row (not above it) so an activity name stays above the fold (J1) */}
                {/* Round 3 Lane 6: below lg the city switch sits here, after the first row, so a card stays above the fold (J1) */}
                {i === 0 && citiesOn && !f.plan && <ChangeCity className="inline-flex lg:hidden" onClick={() => setCityOpen(true)} />}
                {i === 0 && <WeekendEntry />}
                {i === 0 && <TasteEntry />}
              </div>
            ))}

            {view && (
              <section data-review-id="all-listings" aria-labelledby="all-title">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <h2 id="all-title" className="t-section">{f2('browse.all')}</h2>
                  <div className="flex items-center gap-3">
                    <p className="t-meta text-ink-2" aria-live="polite">{resultsLabel(view.total)}</p>
                    {all.length > 0 && toggle}
                  </div>
                </div>
                {all.length === 0 ? (
                  <div data-review-id="browse-empty" className="mt-4 border border-line bg-surface p-5">
                    <p className="t-headline">{f2('browse.empty_title')}</p>
                    <p className="t-body mt-1 text-ink-2">{f2('browse.empty_body')}</p>
                    <EmptyActions reason={view.empty_reason} f={f} atHome={atHome} onWiden={() => setF({ miles: null, anyDist: true })} onClear={() => setF({ ...NO_FILTERS, where: f.where, lat: f.lat, lng: f.lng, miles: f.miles, plan: f.plan, exc: f.exc /* a word they hid stays hidden */ })}
                      onWidenPlan={() => void widenPlan()} />
                  </div>
                ) : view_ === 'map' ? (
                  <div className="mt-4"><BrowseMap cards={all} more={view.next_offset !== null} /></div>
                ) : (
                  <div className="mt-4">
                    <Grid>{groupRepeats(all).map(({ card: c, more }, i) => <ListingCard key={c.item.id} card={c} showDistance={showDistance} more={more} photo={photoRow(all)} row="list" position={i} />)}</Grid>
                  </div>
                )}
                <AnimatePresence>
                  {view.next_offset !== null && (
                    <motion.div exit={{ opacity: 0 }} className="mt-6 flex justify-center">
                      <button type="button" onClick={loadMore} disabled={more} className={buttonClass('secondary', 'lg', 'min-w-[200px]')}>
                        {more && <Spinner size={18} label={false} className="text-current" />}
                        {f2('browse.more')}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            )}
          </motion.div>
        )}
        <FoundDoor />
        <div className="mt-2"><SendFeedback doing="browsing" /></div>{/* F7: under the Settings / Privacy links */}
        <TraySpacer />
        <div aria-hidden="true" className="h-16" />
      </main>
    </BrowseShell>
  )
}

export function LoadingRow({ slow, text, minHeight }: { slow: boolean; text?: string; minHeight?: number }) {
  return (
    <div style={{ minHeight }}>
      <p className="t-meta text-ink-2" role="status" data-review-id={text ? 'where-looking' : undefined}>{text ?? (slow ? f2('browse.slow') : f2('browse.loading'))}</p>
      <div className="-mx-5 mt-4 flex gap-4 overflow-hidden px-5 lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-6 lg:px-0">
        {[0, 1, 2].map((i) => <CardSkeleton key={i} className="w-[326px] shrink-0 lg:w-auto" />)}
      </div>
    </div>
  )
}
